import React, { useState } from 'react';
import { useAuth } from '../../systems/auth-context';
import { apiFetch } from '../../systems/api-client';
import { getRoleLabel } from '../../systems/roles';
import { useAccessibility } from '../../systems/accessibility-context';
import styles from './user-profile.module.css';
import { PlayerAvatar } from '../leaderboard/player-avatar';
import { startRegistration } from '@simplewebauthn/browser';

interface UserProfileProps {
  isOpen: boolean;
  onClose: () => void;
  /** Ouvre l'écran pseudo et couleur (workspaces avec classement). */
  onEditPlayerProfile?: () => void;
}

const UserProfile: React.FC<UserProfileProps> = ({ isOpen, onClose, onEditPlayerProfile }) => {
  const { user, logout } = useAuth();
  const a11y = useAccessibility();
  const [isRegistering, setIsRegistering] = useState(false);
  const [passkeys, setPasskeys] = useState<any[]>([]);
  const [loadingPasskeys, setLoadingPasskeys] = useState(false);
  const [message, setMessage] = useState<{ text: string, type: 'success' | 'error' } | null>(null);

  React.useEffect(() => {
    if (isOpen && user) {
      fetchPasskeys();
    }
  }, [isOpen, user]);

  const fetchPasskeys = async () => {
    setLoadingPasskeys(true);
    try {
      const res = await apiFetch('/api/passkey/list');
      const data = await res.json();
      if (Array.isArray(data)) {
        setPasskeys(data);
      }
    } catch (err) {
      console.error('Failed to fetch passkeys', err);
    } finally {
      setLoadingPasskeys(false);
    }
  };

  const handleDeletePasskey = async (id: string) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette Passkey ?')) return;

    try {
      const res = await apiFetch(`/api/passkey/${id}`, {
        method: 'DELETE',
      });
      const data = await res.json();
      if (data.success) {
        setPasskeys(prev => prev.filter(pk => pk.id !== id));
        setMessage({ text: 'Passkey supprimée', type: 'success' });
      } else {
        throw new Error(data.error || 'Erreur lors de la suppression');
      }
    } catch (err: any) {
      setMessage({ text: err.message, type: 'error' });
    }
  };

  if (!isOpen || !user) return null;

  const handleRegisterPasskey = async () => {
    setIsRegistering(true);
    setMessage(null);
    try {
      const optionsRes = await apiFetch('/api/passkey/register-options');
      const options = await optionsRes.json();

      if (options.error) throw new Error(options.error);

      const attestationResponse = await startRegistration(options);

      const verifyRes = await apiFetch('/api/passkey/register-verify', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(attestationResponse),
      });

      const verification = await verifyRes.json();
      if (verification.verified) {
        setMessage({ text: 'Passkey enregistrée avec succès !', type: 'success' });
        fetchPasskeys(); // Refresh list
      } else {
        throw new Error(verification.error || 'La vérification a échoué');
      }
    } catch (err: any) {
      console.error(err);
      setMessage({ text: `Erreur: ${err.message}`, type: 'error' });
    } finally {
      setIsRegistering(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.profileModal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2>Mon Profil</h2>
          <button className={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div className={styles.content}>
          <div className={styles.infoSection}>
            <div className={styles.infoRow}>
              <span className={styles.label}>Email</span>
              <span className={styles.value}>{user.email}</span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.label}>Rôle</span>
              <span className={styles.value}>{getRoleLabel(user.role)}</span>
            </div>
          </div>

          {user.leaderboard_enabled && (
            <div className={styles.playerProfileSection}>
              <span className={styles.label}>Pseudo du classement</span>
              <div className={styles.playerProfileRow}>
                <PlayerAvatar pseudo={user.pseudo ?? '?'} color={user.avatar_color} size={40} />
                <span className={styles.value}>{user.pseudo ?? 'Pas encore choisi'}</span>
                {onEditPlayerProfile && (
                  <button type="button" className="btn-ghost" onClick={onEditPlayerProfile}>
                    Modifier
                  </button>
                )}
              </div>
            </div>
          )}

          <div className={styles.securitySection}>
            <h3>Sécurité & Passkeys</h3>
            
            {passkeys.length > 0 && (
              <div className={styles.passkeyList}>
                {passkeys.map(pk => (
                  <div key={pk.id} className={styles.passkeyItem}>
                    <div className={styles.passkeyInfo}>
                      <span className={styles.passkeyIcon}>🔑</span>
                      <div className={styles.passkeyMeta}>
                        <span className={styles.passkeyDate}>
                          Créée le {new Date(pk.created_at).toLocaleDateString()}
                        </span>
                        <span className={styles.passkeyId}>
                          ID: {pk.id.substring(0, 10)}...
                        </span>
                      </div>
                    </div>
                    <button 
                      className={styles.deletePasskeyBtn}
                      onClick={() => handleDeletePasskey(pk.id)}
                      title="Supprimer la Passkey"
                    >
                      🗑️
                    </button>
                  </div>
                ))}
              </div>
            )}

            <p className={styles.description}>
              {passkeys.length === 0 
                ? "Configurez une Passkey pour vous connecter sans mot de passe."
                : "Vous pouvez ajouter d'autres Passkeys pour cet appareil ou d'autres."}
            </p>
            
            <button 
              className={styles.passkeyBtn}
              onClick={handleRegisterPasskey}
              disabled={isRegistering}
            >
              {isRegistering ? 'Chargement...' : '➕ Ajouter une Passkey'}
            </button>
            {message && (
              <div className={`${styles.message} ${styles[message.type]}`}>
                {message.text}
              </div>
            )}
          </div>

          <div className={styles.a11ySection}>
            <h3>Accessibilité</h3>
            <label className={styles.checkboxRow}>
              <input
                type="checkbox"
                checked={a11y.strictMode}
                onChange={(e) => a11y.setStrictMode(e.target.checked)}
              />
              <span>Mode accessibilité renforcée pendant les histoires</span>
            </label>
            <p className={styles.description}>
              Pour les personnes malvoyantes : animations désactivées, contraste élevé, texte agrandi et
              affiché immédiatement. Réglage mémorisé sur ce navigateur.
            </p>
          </div>

          <div className={styles.actions}>
            <button className={styles.logoutBtn} onClick={() => {
              logout();
              onClose();
            }}>
              Se déconnecter
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
